<template>
  <div>
    <b-row>
      <b-col>
        <b-table
          class="mt-2"
          id="items"
          striped
          hover
          :busy="loading"
          @row-clicked="onRowClicked"
          :items="references"
          no-local-sorting
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
        >
          <template v-slot:table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong>{{ $t("general.loadingReferences") }}</strong>
            </div>
          </template>
          <template v-slot:[`cell(title.${$i18n.locale})`]="{ item }">{{item.title ? item.title[$i18n.locale] : null}}</template>
          <template v-slot:[`cell(subTitle.${$i18n.locale})`]="{ item }">{{item.subTitle ? item.subTitle[$i18n.locale]:null}}</template>
          <template v-slot:cell(productFamily)="data">
            <b-badge class="m-1 one" v-if="data.value.includes('ONE')">one</b-badge>
            <b-badge class="m-1 nano" v-if="data.value.includes('NANO')">nano</b-badge>
            <b-badge class="m-1 base" v-if="data.value.includes('BASE')">base</b-badge>
            <b-badge class="m-1 carbon" v-if="data.value.includes('CARBON')">carbon</b-badge>
            <b-badge class="m-1 move" v-if="data.value.includes('MOVE')">move</b-badge>
            <b-badge class="m-1 powertrack" v-if="data.value.includes('POWERTRACK')">powertrack</b-badge>
          </template>
          <template
            v-slot:[`cell(location.city)`]="{ item }"
          >{{item.location.city}}, {{item.location.countryCode.toUpperCase()}}</template>
          <template v-slot:[`head(referenceQuality.${qualityLocale})`]>
            <div class="d-flex justify-content-center">
              <b-form-select
                v-if="isAdmin"
                size="sm"
                v-model="qualityLocale"
                :options="languageOptions"
                style="width: 7em"
              ></b-form-select>
              <b-icon class="align-self-center ml-1" icon="star-fill" variant="warning"></b-icon>
            </div>
          </template>
          <template v-slot:[`cell(referenceQuality.${qualityLocale})`]="{ item }">
            <span v-if="item.referenceQuality && item.referenceQuality[qualityLocale] == 6">5+</span>
            <span v-else-if="item.referenceQuality">{{ item.referenceQuality[qualityLocale] }}</span>
            <span v-else>-</span>
          </template>
          <template #cell(functions)="{ item }">
            <b-button-group size="sm">
              <!-- Referenzstatus -->
              <b-button
                class="action-button mr-1"
                :variant="item.released ? 'success' : 'warning'"
                target="_blank"
                @click="releaseReference(item.id, item.released, $event)"
              >
                <b-icon :icon="item.released ? 'check2-all' : 'check2'" variant />
              </b-button>
              <!-- Referenz editieren -->
              <router-link :to="`/references/${item.id}/edit`">
                <b-button class="action-button mr-1" variant="secondary" target="_blank">
                  <b-icon icon="pencil" variant />
                </b-button>
              </router-link>
              <!-- Referenz löschen -->
              <b-button
                class="action-button"
                variant="danger"
                target="_blank"
                @click="deleteReference(item.id)"
              >
                <b-icon icon="trash-fill" variant="light" />
              </b-button>
            </b-button-group>
          </template>
        </b-table>
        <div
          v-if="totalCount > limit"
          class="d-flex flex-row justify-content-center align-items-center"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="limit"
            aria-controls="items"
          ></b-pagination>
        </div>
      </b-col>
    </b-row>
  </div>
</template>
<script>
import { mapActions, mapMutations, mapState } from "vuex";
import currentUserMixin from "@/current-user.js";
import { languageOptions } from "@/languages.js";
import { fetchAPI } from "@/keecloak";
import { extractErrorMessage } from "@/util.js"


export default {
  mixins: [currentUserMixin],
  data: () => ({
    qualityLocale: "de",
    limit: 10,
  }),
  computed: {
    ...mapState("references", [
      "references",
      "totalCount",
      "loading",
      "fetchParams",
    ]),
    ...mapFetchParams(["sortBy", "sortDesc", "offset", "locale"]),
    currentPage: {
      get() {
        return this.offset / this.limit + 1;
      },
      set(value) {
        this.offset = (value - 1) * this.limit;
        this.fetchReferences();
      },
    },
    languageOptions() {
      return languageOptions(this.$i18n.locale)
    },
    fields() {
      let fields = [
        {
          key: `title.${this.$i18n.locale}`,
          label: this.$t("attributes.title"),
          sortable: true,
          thStyle: "width:20%",
        },
        {
          key: `subTitle.${this.$i18n.locale}`,
          label: this.$t("attributes.subTitle"),
          sortable: true,
          thStyle: "width:20%",
          thClass: "d-none d-lg-table-cell",
          tdClass: "d-none d-lg-table-cell"
        },
        {
          key: "productFamily",
          label: this.$t("attributes.productFamily"),
          sortable: false,
        },
        {
          key: "customerName",
          label: this.$t("attributes.customer"),
          sortable: true,
        },
        {
          key: `location.city`,
          label: this.$t("attributes.location"),
          sortable: true,
          thClass: "d-none d-sm-table-cell",
          tdClass: "d-none d-sm-table-cell"
        },
        {
          key: `referenceQuality.${this.qualityLocale}`,
          label: this.$t("attributes.quality"),
          sortable: true,
          class: "text-center",
        },
        {
          key: "functions",
          label: "",
          sortable: false,
          thClass: "d-none d-sm-table-cell",
          tdClass: "d-none d-sm-table-cell"
        },
      ];

      if (!this.isGeneralUser) {
        fields = fields.filter((column) => column.key != "customerName");
      }

      if (!this.isAdmin) {
        fields = fields.filter((column) => column.key != "functions");
      }

      return fields;
    },
  },
  methods: {
    onRowClicked(item) {
      this.$router.push(`/references/${item.id}`);
    },
    async deleteReference(id) {
      const value = await this.$bvModal.msgBoxConfirm(
        this.$t("deleteReferenceQuestion"),
        {
          title: this.$t("deleteReference"),
          okVariant: "danger",
          okTitle: this.$t("yes"),
          cancelTitle: this.$t("no"),
        }
      );
      if (value) {
        const res = await fetchAPI(`references/${id}`, { method: "DELETE" });
        if (res.ok) this.fetchReferences();
        else this.$bvModal.msgBoxOk(
        await extractErrorMessage(res),
        {
          title: this.$t("errors.unableToDeleteReference"),
          bodyTextVariant: "danger",
          okVariant: "danger"
        })

      }
    },
    async releaseReference(id, released) {
      let releaseQuestion;
      if (released) releaseQuestion = "removeReleaseReferenceQuestion";
      else releaseQuestion = "releaseReferenceQuestion";

      const value = await this.$bvModal.msgBoxConfirm(
        this.$t(releaseQuestion),
        {
          title: this.$t("releaseReference"),
          okVariant: "primary",
          okTitle: this.$t("yes"),
          cancelTitle: this.$t("no"),
        }
      );

      if (value) {
        const res = await fetchAPI(
          `references/release/${id}?released=${!released}`,
          { method: "PUT" }
        );
        if (res.ok) this.fetchReferences();
        else this.$bvModal.msgBoxOk(
        await extractErrorMessage(res),
        {
          title: this.$t("errors.unableToVerifyReference"),
          bodyTextVariant: "danger",
          okVariant: "danger"
        });
      }
    },
    ...mapMutations("references", ["setFetchParams"]),
    ...mapActions("references", ["fetchReferences"]),
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.qualityLocale = vm.$i18n.locale
      vm.setFetchParams({
        offset: 0,
        limit: vm.limit,
      });
      vm.fetchReferences();
    });
  },
};

function mapFetchParams(keys) {
  return keys.reduce((result, key) => {
    result[key] = {
      get() {
        return this.fetchParams[key];
      },
      set(value) {
        this.setFetchParams({ [key]: value }), this.fetchReferences();
      },
    };
    return result;
  }, {});
}
</script>
<style lang="scss" scoped>
  .action-button {
    height: 1.8rem;
    display: flex;
    justify-content: center;
    align-items: center;
  }

</style>